import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AlertRule, Signal, SymbolSnapshot, TriggeredAlert } from '../types/market';

const RULES_KEY = 'goldmine-vision-alert-rules';
const HISTORY_KEY = 'goldmine-vision-alert-history';
const COOLDOWN_MS = 15 * 60_000;
const MAX_HISTORY = 100;

export function useAlerts(snapshots: Record<string, SymbolSnapshot>) {
  const [rules, setRules] = useState<AlertRule[]>(() => readJson<AlertRule[]>(RULES_KEY, []));
  const [history, setHistory] = useState<TriggeredAlert[]>(() => readJson<TriggeredAlert[]>(HISTORY_KEY, []));
  const evaluatedRef = useRef<Record<string, number>>({});

  useEffect(() => localStorage.setItem(RULES_KEY, JSON.stringify(rules)), [rules]);
  useEffect(() => localStorage.setItem(HISTORY_KEY, JSON.stringify(history)), [history]);

  const addRule = useCallback((symbol: string, type: AlertRule['type'], value: number | Signal) => {
    const rule: AlertRule = {
      id: crypto.randomUUID(),
      symbol: symbol.toUpperCase(),
      type,
      value,
      enabled: true,
      createdAt: Date.now()
    };
    setRules((current) => [rule, ...current]);
    return rule;
  }, []);

  const removeRule = useCallback((id: string) => setRules((current) => current.filter((rule) => rule.id !== id)), []);
  const toggleRule = useCallback((id: string) => setRules((current) => current.map((rule) => rule.id === id ? { ...rule, enabled: !rule.enabled } : rule)), []);
  const clearHistory = useCallback(() => setHistory([]), []);

  const requestNotifications = useCallback(async () => {
    if (!('Notification' in window)) return 'unsupported' as const;
    if (Notification.permission === 'granted') return 'granted' as const;
    return Notification.requestPermission();
  }, []);

  useEffect(() => {
    const now = Date.now();
    const triggered: TriggeredAlert[] = [];
    const triggeredRuleIds = new Set<string>();

    for (const rule of rules) {
      if (!rule.enabled) continue;
      const snapshot = snapshots[rule.symbol];
      const latest = snapshot?.data?.candles.at(-1);
      if (!latest) continue;
      const snapshotStamp = snapshot.updatedAt ?? snapshot.data?.fetchedAt ?? 0;
      const evalKey = `${rule.id}:${snapshotStamp}`;
      if (evaluatedRef.current[evalKey]) continue;
      evaluatedRef.current[evalKey] = now;

      if (rule.lastTriggeredAt && now - rule.lastTriggeredAt < COOLDOWN_MS) continue;

      let matched = false;
      let message = '';
      if (rule.type === 'PRICE_ABOVE' && typeof rule.value === 'number' && latest.close >= rule.value) {
        matched = true;
        message = `${rule.symbol} is at ${latest.close.toFixed(2)}, above ${rule.value.toFixed(2)}.`;
      } else if (rule.type === 'PRICE_BELOW' && typeof rule.value === 'number' && latest.close <= rule.value) {
        matched = true;
        message = `${rule.symbol} is at ${latest.close.toFixed(2)}, below ${rule.value.toFixed(2)}.`;
      } else if (rule.type === 'SIGNAL' && typeof rule.value === 'string' && snapshot.analysis?.signal === rule.value) {
        matched = true;
        message = `${rule.symbol} strategy signal changed/matched ${rule.value} (${Math.round(snapshot.analysis.confidence * 100)}% confidence).`;
      }

      if (matched) {
        const alert: TriggeredAlert = {
          id: crypto.randomUUID(),
          ruleId: rule.id,
          symbol: rule.symbol,
          message,
          triggeredAt: now
        };
        triggered.push(alert);
        triggeredRuleIds.add(rule.id);
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`GoldMine Vision · ${rule.symbol}`, { body: message, icon: './icons/icon-192.png' });
        }
      }
    }

    if (triggered.length) {
      setHistory((current) => [...triggered, ...current].slice(0, MAX_HISTORY));
      setRules((current) => current.map((rule) => triggeredRuleIds.has(rule.id) ? { ...rule, lastTriggeredAt: now } : rule));
    }
  }, [rules, snapshots]);

  const activeCount = useMemo(() => rules.filter((rule) => rule.enabled).length, [rules]);

  return {
    rules,
    history,
    activeCount,
    addRule,
    removeRule,
    toggleRule,
    clearHistory,
    requestNotifications
  };
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}
